import { z } from "zod";
import { ERROR_CODES, LIMITS } from "../constants";

export const Uuid = z.uuid();

export const IsoDate = z.string().meta({ format: "date-time", example: "2026-10-16T10:00:00.000Z" });

// ---------------------------------------------------------------------------
// Response envelope (D-33)
//
// Every JSON response from /v1 and /admin has the same shape:
//   { "status": "success" | "error", "message": string, "data": ... }
// Errors carry a machine-readable code in data.code.
// ---------------------------------------------------------------------------

export const RESPONSE_STATUSES = ["success", "error"] as const;
export type ResponseStatus = (typeof RESPONSE_STATUSES)[number];

export interface Envelope<T> {
  status: ResponseStatus;
  message: string;
  data: T;
}

/** Success envelope around a data schema. `id` names the component in the OpenAPI spec. */
export function envelope<T extends z.ZodType>(data: T, id: string) {
  return z
    .object({
      status: z.literal("success"),
      message: z.string().meta({ example: "OK" }),
      data,
    })
    .meta({ id });
}

export const ErrorData = z
  .object({
    code: z.union([z.enum(ERROR_CODES), z.string()]).meta({ description: "Machine-readable error code." }),
    details: z.unknown().optional().meta({ description: "Extra context, e.g. validation issues." }),
  })
  .meta({ id: "ErrorData" });

export const ErrorResponse = z
  .object({
    status: z.literal("error"),
    message: z.string(),
    data: ErrorData,
  })
  .meta({
    id: "ErrorResponse",
    example: {
      status: "error",
      message: "Credential 'sms-main' is not assigned to this app",
      data: { code: "credential_not_allowed" },
    },
  });
export type ErrorResponse = z.infer<typeof ErrorResponse>;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const PageQuery = z.object({
  cursor: z.string().optional().meta({ description: "The `next_cursor` value from the previous page." }),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(LIMITS.pageSizeDefault),
});
export type PageQuery = z.infer<typeof PageQuery>;

/** Cursor page: `{ items, next_cursor }`. `next_cursor` is null on the last page. */
export function paginated<T extends z.ZodType>(item: T, id: string) {
  return z
    .object({
      items: z.array(item),
      next_cursor: z.string().nullable(),
    })
    .meta({ id });
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}
